import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Bot, ArrowRight, CheckCircle, Zap, MessageSquare, BarChart3, Workflow, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageSEO } from "@/components/PageSEO";

export default function AIAutomationPage() {
  return (
    <div data-testid="ai-automation-page" className="pt-16 lg:pt-20">
      <PageSEO
        title="AI Agents & Automation Solutions UK"
        description="Custom AI agents, chatbots, AI receptionists and workflow automation for UK businesses. Streamline operations and reduce costs. Get a free consultation."
        path="/ai-automation-agents-uk"
      />

      {/* Hero */}
      <section className="py-20 lg:py-28 hero-gradient relative overflow-hidden">
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="text-cyan-400 text-sm font-medium uppercase tracking-widest mb-4 block">AI Agents & Automation</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6">
              AI Agents & Automation <span className="gradient-text">for UK Businesses</span>
            </h1>
            <p className="text-lg text-gray-400 mb-8 leading-relaxed max-w-2xl">
              Stop wasting hours on repetitive tasks. Our custom AI agents and automation solutions handle enquiries, manage workflows, and free your team to focus on growing your business.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button className="btn-primary text-white font-medium px-8 py-3">
                  Book a Free Consultation <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-white/5 px-8 py-3">
                  Get a Quote
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Are AI Agents */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">What Are AI Agents?</h2>
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <p className="text-gray-400 leading-relaxed mb-4">
                  AI agents are intelligent software systems that can understand, reason, and take action on behalf of your business. Unlike traditional software that follows rigid rules, AI agents learn from context and adapt to each situation — answering customer enquiries, processing data, scheduling appointments, and managing entire workflows without human intervention.
                </p>
                <p className="text-gray-400 leading-relaxed mb-4">
                  At Your-IT-Solutions, based in Lowestoft, Suffolk, we design and deploy custom AI agents tailored to your specific business needs. Whether you run a local service company in East Anglia or operate nationwide across the UK, our solutions scale with you.
                </p>
                <p className="text-gray-400 leading-relaxed">
                  From AI receptionists that handle phone and web enquiries around the clock, to workflow automation that eliminates manual data entry, we build solutions that deliver measurable results from day one.
                </p>
              </div>
              <div className="space-y-4">
                <div className="p-5 rounded-xl bg-gray-900/50 border border-gray-800">
                  <div className="flex items-center gap-3 mb-2">
                    <MessageSquare size={20} className="text-cyan-500" />
                    <h3 className="text-white font-semibold">AI Receptionists</h3>
                  </div>
                  <p className="text-gray-400 text-sm">Handle customer calls, live chat, and web enquiries 24/7. Never miss a lead again.</p>
                </div>
                <div className="p-5 rounded-xl bg-gray-900/50 border border-gray-800">
                  <div className="flex items-center gap-3 mb-2">
                    <Workflow size={20} className="text-cyan-500" />
                    <h3 className="text-white font-semibold">Workflow Automation</h3>
                  </div>
                  <p className="text-gray-400 text-sm">Automate invoicing, scheduling, data entry, and internal processes to save hours every week.</p>
                </div>
                <div className="p-5 rounded-xl bg-gray-900/50 border border-gray-800">
                  <div className="flex items-center gap-3 mb-2">
                    <BarChart3 size={20} className="text-cyan-500" />
                    <h3 className="text-white font-semibold">AI-Powered Analytics</h3>
                  </div>
                  <p className="text-gray-400 text-sm">Get actionable insights from your business data without hiring a data analyst.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Businesses Need Automation */}
      <section className="py-16 lg:py-24 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Why UK Businesses Are Adopting AI Automation</h2>
            <p className="text-gray-400 mb-12 max-w-2xl text-lg">
              Businesses across the United Kingdom are turning to AI to stay competitive, reduce overheads, and deliver better customer experiences.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="p-6 rounded-2xl bg-gray-900/50 border border-gray-800">
                <Zap size={28} className="text-cyan-500 mb-4" />
                <h3 className="text-white font-semibold text-lg mb-2">Reduce Operational Costs</h3>
                <p className="text-gray-400 text-sm leading-relaxed">Automating repetitive tasks can reduce operational costs by up to 40%. AI agents work around the clock without overtime, sick days, or training costs — freeing your budget for growth.</p>
              </div>
              <div className="p-6 rounded-2xl bg-gray-900/50 border border-gray-800">
                <MessageSquare size={28} className="text-cyan-500 mb-4" />
                <h3 className="text-white font-semibold text-lg mb-2">Never Miss a Lead</h3>
                <p className="text-gray-400 text-sm leading-relaxed">An AI receptionist responds to enquiries instantly — whether it is midnight or a bank holiday. Every potential customer gets a fast, professional response that converts interest into action.</p>
              </div>
              <div className="p-6 rounded-2xl bg-gray-900/50 border border-gray-800">
                <Shield size={28} className="text-cyan-500 mb-4" />
                <h3 className="text-white font-semibold text-lg mb-2">Scale Without Extra Headcount</h3>
                <p className="text-gray-400 text-sm leading-relaxed">Growing your business should not mean proportionally growing your admin team. AI automation handles increased volume effortlessly, letting you scale operations while keeping your team lean.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our AI Solutions */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-12">Our AI & Automation Solutions</h2>
            <div className="space-y-12">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-4">Custom AI Chatbots & Virtual Assistants</h3>
                  <p className="text-gray-400 leading-relaxed mb-4">
                    We build intelligent chatbots that understand your business and communicate naturally with your customers. Deployed on your website, social media, or internal systems, these virtual assistants handle common questions, qualify leads, and book appointments — all without human input.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-gray-400 text-sm"><CheckCircle size={16} className="text-cyan-500 flex-shrink-0" /> Natural language understanding for human-like conversations</li>
                    <li className="flex items-center gap-2 text-gray-400 text-sm"><CheckCircle size={16} className="text-cyan-500 flex-shrink-0" /> Trained on your specific products, services, and FAQs</li>
                    <li className="flex items-center gap-2 text-gray-400 text-sm"><CheckCircle size={16} className="text-cyan-500 flex-shrink-0" /> Seamless handoff to human agents when needed</li>
                    <li className="flex items-center gap-2 text-gray-400 text-sm"><CheckCircle size={16} className="text-cyan-500 flex-shrink-0" /> Integration with your CRM, email, and booking systems</li>
                  </ul>
                </div>
                <div className="p-8 rounded-2xl bg-gray-900/50 border border-gray-800">
                  <Bot size={48} className="text-cyan-500 mb-4" />
                  <p className="text-gray-400 italic text-sm">"Since implementing an AI chatbot from Your-IT-Solutions, we respond to enquiries within seconds instead of hours. Our conversion rate has improved significantly."</p>
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div className="lg:order-2">
                  <h3 className="text-2xl font-bold text-white mb-4">AI Receptionist Services</h3>
                  <p className="text-gray-400 leading-relaxed mb-4">
                    Your AI receptionist answers calls, greets website visitors, qualifies enquiries, and routes them to the right person or department. It works 24 hours a day, 7 days a week, ensuring no opportunity slips through the cracks. Perfect for small businesses in Lowestoft, East Anglia, or anywhere in the UK that need professional front-of-house presence without the overhead.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-gray-400 text-sm"><CheckCircle size={16} className="text-cyan-500 flex-shrink-0" /> 24/7 availability across phone, web, and messaging</li>
                    <li className="flex items-center gap-2 text-gray-400 text-sm"><CheckCircle size={16} className="text-cyan-500 flex-shrink-0" /> Appointment scheduling and calendar management</li>
                    <li className="flex items-center gap-2 text-gray-400 text-sm"><CheckCircle size={16} className="text-cyan-500 flex-shrink-0" /> Lead qualification and routing</li>
                    <li className="flex items-center gap-2 text-gray-400 text-sm"><CheckCircle size={16} className="text-cyan-500 flex-shrink-0" /> Professional, branded voice and messaging</li>
                  </ul>
                </div>
                <div className="lg:order-1 p-8 rounded-2xl bg-gray-900/50 border border-gray-800">
                  <Zap size={48} className="text-purple-500 mb-4" />
                  <h3 className="text-white font-semibold mb-2">Key Results</h3>
                  <p className="text-gray-400 text-sm">Businesses using AI receptionists report up to 60% fewer missed calls and a 35% increase in booked appointments.</p>
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-4">Workflow & Process Automation</h3>
                  <p className="text-gray-400 leading-relaxed mb-4">
                    We identify the manual, time-consuming processes in your business and automate them. From invoice processing and inventory management to employee onboarding and report generation — if a human is doing it repeatedly, an AI agent can likely do it faster and more accurately.
                  </p>
                  <p className="text-gray-400 leading-relaxed">
                    Our automation solutions integrate with tools you already use — Microsoft 365, CRM systems, accounting software, and more. We build bridges between your existing systems so data flows automatically, errors decrease, and your team spends time on work that actually requires human judgement.
                  </p>
                </div>
                <div className="p-8 rounded-2xl bg-gray-900/50 border border-gray-800">
                  <Workflow size={48} className="text-green-500 mb-4" />
                  <h3 className="text-white font-semibold mb-3">Common Automations</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-gray-400 text-sm"><CheckCircle size={16} className="text-cyan-500 flex-shrink-0" /> Automated invoice processing and reminders</li>
                    <li className="flex items-center gap-2 text-gray-400 text-sm"><CheckCircle size={16} className="text-cyan-500 flex-shrink-0" /> Customer onboarding workflows</li>
                    <li className="flex items-center gap-2 text-gray-400 text-sm"><CheckCircle size={16} className="text-cyan-500 flex-shrink-0" /> Automated reporting and dashboards</li>
                    <li className="flex items-center gap-2 text-gray-400 text-sm"><CheckCircle size={16} className="text-cyan-500 flex-shrink-0" /> Email and follow-up sequences</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-16 lg:py-24 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-12 text-center">How We Work</h2>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-cyan-400 font-bold">1</span>
                </div>
                <h3 className="text-white font-semibold mb-2">Discovery</h3>
                <p className="text-gray-400 text-sm">We learn your business processes and identify the biggest automation opportunities.</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-cyan-400 font-bold">2</span>
                </div>
                <h3 className="text-white font-semibold mb-2">Design</h3>
                <p className="text-gray-400 text-sm">We design the AI agent or automation workflow, tailored to your exact requirements.</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-cyan-400 font-bold">3</span>
                </div>
                <h3 className="text-white font-semibold mb-2">Build & Test</h3>
                <p className="text-gray-400 text-sm">We build, train, and rigorously test the solution before it goes live.</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-cyan-400 font-bold">4</span>
                </div>
                <h3 className="text-white font-semibold mb-2">Launch & Support</h3>
                <p className="text-gray-400 text-sm">We deploy the solution and provide ongoing monitoring and optimisation.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Ready to Automate Your Business?</h2>
            <p className="text-gray-400 mb-8 text-lg">
              Based in Lowestoft, Suffolk, we work with businesses across East Anglia and the entire UK. Book a free consultation to discover how AI can transform your operations.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button className="btn-primary text-white font-medium px-8 py-3">
                  Book a Free Consultation <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <Link to="/website-design-uk">
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-white/5 px-8 py-3">
                  See Our Other Services
                </Button>
              </Link>
            </div>
            <p className="text-gray-500 text-sm mt-6">
              We also offer <Link to="/website-design-uk" className="text-cyan-400 hover:text-cyan-300">website design</Link> services.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
